import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { api } from "@/lib/http.ts";
import { messageQueries } from "@/shared/messageQueries.ts";
import { userReactionLabels } from "@/shared/reactionLabels.ts";
import { userReactionEmojis } from "@/types/reactions.ts";
import type { NewMessageReq } from "@/types/threadEvents.ts";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EmojiPicker } from "frimousse";
import { Plus, SmilePlus } from "lucide-react";
import { useState } from "react";
import { ReactionBadge, reactionPillClassName } from "./ReactionBadge.tsx";

/**
 * Where the full picker loads its emoji from. Pinned so a new emojibase release
 * can't change the data under us; frimousse would otherwise read `@latest`.
 */
const EMOJIBASE_URL = "https://cdn.jsdelivr.net/npm/emojibase-data@17.0.0";

/**
 * Put the user's emoji on one of okthx's replies. Sent like any message — the
 * emoji as its text, `autoReaction` as its type, the reply in `replyToMessageId` — and
 * the agent is woken like for any message. The feed draws it
 * as a badge on the reply once the thread refetches, never as a message.
 */
const useReactToMessage = (threadId: number) => {
  const queryClient = useQueryClient();
  const refreshThread = async () =>
    queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) });

  return useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: number; emoji: string }) => {
      const body: NewMessageReq = {
        text: emoji,
        type: "autoReaction",
        replyToMessageId: messageId,
      };
      await api.post(`/api/a/threads/${threadId}/messages`, body);
    },
    onSuccess: refreshThread,
    onError: (error) => {
      Sentry.captureException(error, { tags: { action: "message-react" } });
      errorToast("Couldn't add your reaction");
      void refreshThread();
    },
  });
};

/**
 * The control that puts the user's emoji on a reply, and the row of emoji it
 * opens, ending in a + that swaps the row for every emoji. Picking one sends it
 * and closes the popover.
 *
 * Before the user reacts, it is the add-reaction button in the reply's footer,
 * left of copy. After, there is nothing to add, only something to change, so
 * the user's reaction pill itself is the button. Picking the emoji the reply
 * already has only closes it, since sending it again would wake the agent for
 * nothing.
 */
export const ReactionPicker = ({
  threadId,
  messageId,
  currentEmoji,
  onOpenChange,
}: {
  threadId: number;
  messageId: number;
  /** The user's reaction on this reply now, if any. */
  currentEmoji?: string;
  /**
   * Told when the popover opens and closes, so a hover-only footer holding this
   * button can stay up while the user picks.
   */
  onOpenChange?: (open: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { mutate: react, isPending } = useReactToMessage(threadId);

  const handlePick = (emoji: string) => {
    handleOpenChange(false);
    if (emoji !== currentEmoji) react({ messageId, emoji });
  };

  // Reopening starts from the quick row again, whatever was showing last time.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
    if (!next) setShowAll(false);
  };

  // Not a button while the reaction is on its way, so a second tap can't send
  // another before the new badge arrives. The current reaction stays in view.
  if (isPending) {
    return currentEmoji ? (
      <ReactionBadge reactions={[currentEmoji]} reactor="you" className="static" />
    ) : null;
  }

  const label = currentEmoji ? "Change reaction" : "Add reaction";
  return (
    <div className="text-xs text-muted-foreground">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              {currentEmoji ? (
                <button
                  type="button"
                  className={cn(
                    reactionPillClassName,
                    "cursor-pointer hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                  )}
                  aria-label={`${label}, you reacted ${currentEmoji}`}
                  data-statsig-name="message-reaction-change"
                >
                  {currentEmoji}
                </button>
              ) : (
                <Button
                  size="fit"
                  variant="ghost"
                  className="size-5"
                  aria-label={label}
                  data-statsig-name="action-bar-react"
                >
                  <SmilePlus className="size-3" />
                </Button>
              )}
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{label}</TooltipContent>
        </Tooltip>
        {/* Rounded like the thread's artifact chips, so it reads as the same surface. */}
        <PopoverContent className="w-auto rounded-xl p-1" align="start" side="top">
          {showAll ? (
            <AllEmojiPicker onPick={handlePick} />
          ) : (
            <div className="flex gap-0.5">
              {userReactionEmojis.map((emoji) => (
                <Button
                  key={emoji}
                  size="fit"
                  variant="ghost"
                  className="size-8 text-lg leading-none"
                  aria-label={userReactionLabels[emoji]}
                  data-statsig-name="message-reaction-pick"
                  onClick={() => handlePick(emoji)}
                >
                  {emoji}
                </Button>
              ))}
              <Button
                size="fit"
                variant="ghost"
                className="size-8 text-muted-foreground"
                aria-label="More reactions"
                data-statsig-name="message-reaction-more"
                onClick={() => setShowAll(true)}
              >
                <Plus className="size-4" />
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

/** Every emoji, searchable and grouped by category, for the + in the quick row. */
const AllEmojiPicker = ({ onPick }: { onPick: (emoji: string) => void }) => (
  <EmojiPicker.Root
    className="flex h-80 w-72 flex-col"
    emojibaseUrl={EMOJIBASE_URL}
    onEmojiSelect={({ emoji }) => onPick(emoji)}
  >
    <EmojiPicker.Search
      autoFocus
      className="mx-1 mt-1 mb-2 h-8 rounded-md border bg-background px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
    <EmojiPicker.Viewport className="relative flex-1 outline-none">
      <EmojiPicker.Loading className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </EmojiPicker.Loading>
      <EmojiPicker.Empty className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
        No emoji found.
      </EmojiPicker.Empty>
      <EmojiPicker.List
        className="select-none pb-1"
        components={{
          CategoryHeader: ({ category, ...props }) => (
            <div
              className="bg-popover px-2 pt-2 pb-1 text-xs font-medium text-muted-foreground"
              {...props}
            >
              {category.label}
            </div>
          ),
          Row: ({ children, ...props }) => (
            <div className="scroll-my-1 px-1" {...props}>
              {children}
            </div>
          ),
          Emoji: ({ emoji, ...props }) => (
            <Button
              size="fit"
              variant="ghost"
              className="size-8 text-lg leading-none data-active:bg-accent"
              data-statsig-name="message-reaction-pick-all"
              {...props}
            >
              {emoji.emoji}
            </Button>
          ),
        }}
      />
    </EmojiPicker.Viewport>
  </EmojiPicker.Root>
);
